import { Box, HStack, Link, Text } from '@chakra-ui/react';
import { IconEmail2 } from 'central-icons/IconEmail2';
import { IconTelephone } from 'central-icons/IconTelephone';

import { SmallLinkedinIcon } from '@revelio/assets';

import { normalizeProfileUrl } from '../../../utils/normalizeProfileUrl';
import BlurredCell from '../../BlurredCell';

interface ContactInfoSectionProps {
  email?: string;
  personalEmail?: string;
  phoneNumber?: string;
  profileUrl?: string;
  isTrialUser?: boolean;
  encryptedEmail?: string;
  encryptedPersonalEmail?: string;
  encryptedPhoneNumber?: string;
  encryptedProfileUrl?: string;
  userKey?: string;
  onBlurHover?: () => void;
}

const noop = () => undefined;

export const ContactInfoSection = ({
  email,
  personalEmail,
  phoneNumber,
  profileUrl,
  isTrialUser = false,
  encryptedEmail,
  encryptedPersonalEmail,
  encryptedPhoneNumber,
  encryptedProfileUrl,
  userKey,
  onBlurHover,
}: ContactInfoSectionProps) => {
  const normalizedProfileUrl =
    profileUrl && profileUrl.trim() ? normalizeProfileUrl(profileUrl) : null;
  const hasAnyContact =
    Boolean(email?.trim()) ||
    Boolean(personalEmail?.trim()) ||
    Boolean(phoneNumber?.trim()) ||
    Boolean(normalizedProfileUrl);

  if (!hasAnyContact) return null;

  const iconBoxProps = {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    lineHeight: 0,
    alignSelf: 'center',
  };

  return (
    <HStack
      columnGap={4}
      rowGap={2}
      fontSize="sm"
      flexWrap="wrap"
      alignItems="center"
      color="text.primary"
    >
      {email && (
        <HStack spacing={2} data-testid="card-email">
          <Box flexShrink={0} mt="2px">
            <IconEmail2 size={16} />
          </Box>
          {isTrialUser ? (
            <BlurredCell
              value={email}
              encryptedValue={encryptedEmail}
              onMouseEnter={onBlurHover ?? noop}
              allowUnblur={false}
              isCompact={false}
              userKey={userKey}
              fieldKey="email"
            />
          ) : (
            <Link
              href={`mailto:${email}`}
              color="green.600"
              _hover={{ textDecoration: 'underline' }}
              onClick={(e) => e.stopPropagation()}
            >
              {email}
            </Link>
          )}
        </HStack>
      )}

      {personalEmail && (
        <HStack
          spacing={2}
          data-testid="card-personal-email"
          color="text.primary"
        >
          <Box flexShrink={0} mt="2px">
            <IconEmail2 size={16} />
          </Box>
          {isTrialUser ? (
            <BlurredCell
              value={personalEmail}
              encryptedValue={encryptedPersonalEmail}
              onMouseEnter={onBlurHover ?? noop}
              allowUnblur={false}
              isCompact={false}
              userKey={userKey}
              fieldKey="personal_email"
            />
          ) : (
            <Link
              href={`mailto:${personalEmail}`}
              color="green.600"
              _hover={{ textDecoration: 'underline' }}
              onClick={(e) => e.stopPropagation()}
            >
              {personalEmail}
            </Link>
          )}
        </HStack>
      )}

      {phoneNumber && (
        <HStack spacing={2} data-testid="card-phone-number">
          <Box flexShrink={0} mt="2px">
            <IconTelephone size={16} />
          </Box>
          {isTrialUser ? (
            <BlurredCell
              value={phoneNumber}
              encryptedValue={encryptedPhoneNumber}
              onMouseEnter={onBlurHover ?? noop}
              allowUnblur={false}
              isCompact={false}
              userKey={userKey}
              fieldKey="phone_number"
            />
          ) : (
            <Text color="green.600">{phoneNumber}</Text>
          )}
        </HStack>
      )}

      {normalizedProfileUrl && (
        <HStack spacing={2} data-testid="card-profile-url">
          <Box {...iconBoxProps}>
            <SmallLinkedinIcon
              width="12px"
              height="12px"
              color="currentColor"
              display="block"
            />
          </Box>
          {isTrialUser ? (
            <BlurredCell
              value={normalizedProfileUrl.displayValue}
              encryptedValue={encryptedProfileUrl}
              onMouseEnter={onBlurHover ?? noop}
              allowUnblur={false}
              isCompact={false}
              userKey={userKey}
              fieldKey="linkedin_url"
            />
          ) : (
            <Link
              href={normalizedProfileUrl.href}
              color="green.600"
              _hover={{ textDecoration: 'underline' }}
              onClick={(e) => e.stopPropagation()}
              isExternal
              rel="noopener noreferrer"
            >
              {normalizedProfileUrl.displayValue}
            </Link>
          )}
        </HStack>
      )}
    </HStack>
  );
};
